import { useState, useEffect, useCallback } from 'react';
import { findScreenByName, getNextScreenFromTransition } from './flowEngine';

export function useNavigation({ data }) {
    const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
    const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
    const [navigationHistory, setNavigationHistory] = useState([]);

    // popstate handler
    useEffect(() => {
        const handlePopState = (event) => {
            if (event.state && event.state.groupIndex !== undefined && event.state.screenIndex !== undefined) {
                setCurrentGroupIndex(event.state.groupIndex);
                setCurrentScreenIndex(event.state.screenIndex);
            } else {
                const hash = window.location.hash.substring(1);
                if (hash && data) {
                    const pos = findScreenByName(hash, data);
                    if (pos) {
                        setCurrentGroupIndex(pos.groupIndex);
                        setCurrentScreenIndex(pos.screenIndex);
                    }
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [data]);

    const getCurrentScreen = useCallback(() => {
        if (!data || !data.groups) return null;
        const currentGroup = data.groups[currentGroupIndex];
        if (!currentGroup || !currentGroup.screens) return null;
        return currentGroup.screens[currentScreenIndex];
    }, [data, currentGroupIndex, currentScreenIndex]);

    const getTotalScreens = useCallback(() => {
        if (!data || !data.groups) return 0;
        return data.groups.reduce((total, group) => total + group.screens.length, 0);
    }, [data]);

    const getCurrentScreenPosition = useCallback(() => {
        if (!data || !data.groups) return 0;
        let position = 0;
        for (let i = 0; i < currentGroupIndex; i++) {
            position += data.groups[i].screens.length;
        }
        return position + currentScreenIndex;
    }, [data, currentGroupIndex, currentScreenIndex]);

    const updateURL = useCallback(() => {
        if (!data || !data.groups) return;

        const currentGroup = data.groups[currentGroupIndex];
        if (!currentGroup || !currentGroup.screens) return;

        const currentScreen = currentGroup.screens[currentScreenIndex];
        if (currentScreen && currentScreen.name) {
            const newHash = `#${currentScreen.name}`;
            if (window.location.hash !== newHash) {
                window.history.pushState(
                    { groupIndex: currentGroupIndex, screenIndex: currentScreenIndex },
                    '',
                    newHash
                );
            }
        }
    }, [data, currentGroupIndex, currentScreenIndex]);

    const initializeFromURL = useCallback((boardData) => {
        const hash = window.location.hash.substring(1);
        if (hash && boardData) {
            const pos = findScreenByName(hash, boardData);
            if (pos) {
                setCurrentGroupIndex(pos.groupIndex);
                setCurrentScreenIndex(pos.screenIndex);
                window.history.replaceState(
                    { groupIndex: pos.groupIndex, screenIndex: pos.screenIndex },
                    '',
                    window.location.hash
                );
            } else {
                const firstScreen = boardData.groups[0]?.screens[0];
                if (firstScreen && firstScreen.name) {
                    window.history.replaceState({ groupIndex: 0, screenIndex: 0 }, '', `#${firstScreen.name}`);
                }
            }
        } else {
            const firstScreen = boardData.groups[0]?.screens[0];
            if (firstScreen && firstScreen.name) {
                window.history.replaceState({ groupIndex: 0, screenIndex: 0 }, '', `#${firstScreen.name}`);
            }
        }
    }, []);

    const pushHistory = useCallback(() => {
        setNavigationHistory(prev => [...prev, { groupIndex: currentGroupIndex, screenIndex: currentScreenIndex }]);
    }, [currentGroupIndex, currentScreenIndex]);

    const goToNextScreen = useCallback(({
        skipValidation = false,
        globalAnswersRef,
        formData,
        hasRequiredFields,
        validateRequiredFields,
        showValidationError,
        onScreenWillComplete,
        onboardingId,
    } = {}) => {
        const currentScreen = getCurrentScreen();

        if (!skipValidation && currentScreen && hasRequiredFields(currentScreen)) {
            if (!validateRequiredFields(currentScreen, formData)) {
                showValidationError(currentScreen, formData);
                return;
            }
        }

        if (onScreenWillComplete) {
            onScreenWillComplete(currentScreen);
        }

        setNavigationHistory(prev => [...prev, { groupIndex: currentGroupIndex, screenIndex: currentScreenIndex }]);

        const targetScreenName = getNextScreenFromTransition(currentScreen, globalAnswersRef);
        if (targetScreenName) {
            const pos = findScreenByName(targetScreenName, data);
            if (pos) {
                setCurrentGroupIndex(pos.groupIndex);
                setCurrentScreenIndex(pos.screenIndex);
                return;
            }
            console.warn(`Transition target screen "${targetScreenName}" not found, falling back to sequential navigation`);
        }

        const currentGroup = data.groups[currentGroupIndex];

        if (currentScreenIndex < currentGroup.screens.length - 1) {
            setCurrentScreenIndex(prev => prev + 1);
        } else if (currentGroupIndex < data.groups.length - 1) {
            setCurrentGroupIndex(prev => prev + 1);
            setCurrentScreenIndex(0);
        } else {
            console.log('Onboarding completed!');
        }
    }, [data, currentGroupIndex, currentScreenIndex, getCurrentScreen]);

    const goToPreviousScreen = useCallback(() => {
        if (navigationHistory.length > 0) {
            const newHistory = [...navigationHistory];
            const previous = newHistory.pop();
            setNavigationHistory(newHistory);
            setCurrentGroupIndex(previous.groupIndex);
            setCurrentScreenIndex(previous.screenIndex);
        } else {
            console.log('No navigation history, already at first screen');
        }
    }, [navigationHistory]);

    const goToScreenWithName = useCallback((name, skipHistory = false) => {
        if (!data || !data.groups) {
            console.error('Data not loaded yet');
            return false;
        }

        const pos = findScreenByName(name, data);
        if (pos) {
            if (!skipHistory) {
                setNavigationHistory(prev => [...prev, { groupIndex: currentGroupIndex, screenIndex: currentScreenIndex }]);
            }
            setCurrentGroupIndex(pos.groupIndex);
            setCurrentScreenIndex(pos.screenIndex);
            return true;
        } else {
            console.error(`Screen with name "${name}" not found`);
            return false;
        }
    }, [data, currentGroupIndex, currentScreenIndex]);

    return {
        currentGroupIndex,
        currentScreenIndex,
        navigationHistory,
        getCurrentScreen,
        getTotalScreens,
        getCurrentScreenPosition,
        updateURL,
        initializeFromURL,
        goToNextScreen,
        goToPreviousScreen,
        goToScreenWithName,
    };
}
